/**
 * Ancho y estado (plegado a riel) de la mitad izquierda de la ventana de documento (DESIGN.md §11.7): entre 320 y
 * 640 px, 470 por defecto, riel de 56 px. Se recuerdan por persona en `localStorage`. El atajo «[» pliega y despliega
 * fuera de un campo de texto.
 */
import { useCallback, useEffect, useState } from 'react';

export const ANCHO_MIN = 320;
export const ANCHO_MAX = 640;
export const ANCHO_DEFECTO = 470;
export const ANCHO_RIEL = 56;

export interface EstadoPanel { ancho: number; plegado: boolean }

const clave = (usuarioId?: string) => `alertai_panel_doc_${usuarioId || 'anon'}`;
export const acotar = (n: number) => Math.min(ANCHO_MAX, Math.max(ANCHO_MIN, Math.round(n)));

function leer(usuarioId?: string): EstadoPanel {
  try {
    const crudo = window.localStorage.getItem(clave(usuarioId));
    if (crudo) {
      const v = JSON.parse(crudo) as Partial<EstadoPanel>;
      return { ancho: typeof v.ancho === 'number' ? acotar(v.ancho) : ANCHO_DEFECTO, plegado: !!v.plegado };
    }
  } catch { /* sin almacenamiento: valores por defecto */ }
  return { ancho: ANCHO_DEFECTO, plegado: false };
}

/** Un campo de texto o un elemento editable: ahí «[» se escribe, no pliega. */
export function enCampoDeTexto(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** `activo`: el atajo «[» solo vale cuando el panel v5 está en pantalla (documentos del SII). */
export function usePanelDocumento(usuarioId?: string, activo = true) {
  const [estado, setEstado] = useState<EstadoPanel>(() => leer(usuarioId));
  // Al cambiar de persona (otra sesión) se relee su valor.
  useEffect(() => { setEstado(leer(usuarioId)); }, [usuarioId]);
  useEffect(() => {
    try { window.localStorage.setItem(clave(usuarioId), JSON.stringify(estado)); } catch { /* sin almacenamiento */ }
  }, [estado, usuarioId]);

  const setAncho = useCallback((n: number) => setEstado(e => ({ ...e, ancho: acotar(n) })), []);
  const setPlegado = useCallback((plegado: boolean) => setEstado(e => ({ ...e, plegado })), []);
  const alternar = useCallback(() => setEstado(e => ({ ...e, plegado: !e.plegado })), []);

  useEffect(() => {
    if (!activo) return;
    const tecla = (e: KeyboardEvent) => {
      // En teclados latinoamericanos «[» se escribe con AltGr, que llega como Ctrl+Alt: ese caso sí vale.
      const altGr = typeof e.getModifierState === 'function' && e.getModifierState('AltGraph');
      if (e.key !== '[' || (!altGr && (e.ctrlKey || e.metaKey || e.altKey)) || enCampoDeTexto(e.target)) return;
      e.preventDefault();
      alternar();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [alternar, activo]);

  return { ancho: estado.ancho, plegado: estado.plegado, setAncho, setPlegado, alternar };
}
